import { useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  Button,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag
} from 'antd'
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined
} from '@ant-design/icons'
import MainTitle from 'components/MainTitle'
import Spinner from 'components/spinner'
import { useBrandList, useDepartmentList } from 'store/server/useBrand'
import {
  useAddSizeToGroup,
  useCreateSizeGroup,
  useDeleteSizeFromGroup,
  useDeleteSizeGroup,
  useDeleteSizeRow,
  useSizeGroup,
  useSizeGroupList,
  useUpdateSizeGroup,
  useUpdateSizeInGroup,
  useUpdateSizeRow
} from 'store/server/useSizeGroup'
import {
  apiBrandFromParam,
  brandLabel,
  formValuesToSizePayload,
  formatCell,
  groupSizeCount,
  isHomeBrand,
  sizeFieldsFor,
  sizeToFormValues,
  slugFromBrandName,
  tableColumnsFor
} from './fields'

export default function SizeGroupManagement() {
  const { brand } = useParams()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedId = searchParams.get('id') || ''

  const [searchText, setSearchText] = useState('')
  const [sizeSearch, setSizeSearch] = useState('')
  const [groupModalOpen, setGroupModalOpen] = useState(false)
  const [editingGroup, setEditingGroup] = useState(null)
  const [sizeModalOpen, setSizeModalOpen] = useState(false)
  const [editingSize, setEditingSize] = useState(null)
  const [groupForm] = Form.useForm()
  const [sizeForm] = Form.useForm()

  const brandForApi = apiBrandFromParam(brand)
  const home = isHomeBrand(brandForApi)
  const fields = sizeFieldsFor(brandForApi)
  const tableCols = tableColumnsFor(brandForApi)

  const { data: brands = [] } = useBrandList()
  const brandRecord = useMemo(
    () =>
      brands.find((item) => {
        const name = String(item.name || '').toLowerCase()
        if (brandForApi === 'zara') return name === 'zara'
        return name.replace(/\s+/g, '') === 'zarahome' || name.includes('home')
      }),
    [brands, brandForApi]
  )
  const { data: departments = [] } = useDepartmentList(brandRecord?.id)
  const { data: sizeGroups = [], isLoading } = useSizeGroupList(brandForApi)
  const { data: sizeGroupDetail, isFetching: detailLoading } = useSizeGroup(selectedId, brandForApi)

  const createMutation = useCreateSizeGroup()
  const updateMutation = useUpdateSizeGroup()
  const deleteMutation = useDeleteSizeGroup()
  const addSizeMutation = useAddSizeToGroup()
  const updateSizeMutation = useUpdateSizeInGroup()
  const deleteSizeMutation = useDeleteSizeFromGroup()
  const updateSizeRowMutation = useUpdateSizeRow()
  const deleteSizeRowMutation = useDeleteSizeRow()

  const brandTabs = useMemo(
    () =>
      (brands || []).filter((item) => {
        const name = String(item.name || '').toLowerCase()
        return name === 'zara' || name.replace(/\s+/g, '') === 'zarahome' || name.includes('home')
      }),
    [brands]
  )

  const filteredGroups = useMemo(() => {
    const q = searchText.trim().toLowerCase()
    if (!q) return sizeGroups
    return sizeGroups.filter((item) => {
      return (
        item.name?.toLowerCase().includes(q) ||
        item.id?.toLowerCase().includes(q) ||
        item.variant?.toLowerCase().includes(q) ||
        item.Department?.name?.toLowerCase().includes(q)
      )
    })
  }, [sizeGroups, searchText])

  const selectedSummary = useMemo(
    () => sizeGroups.find((item) => item.id === selectedId) || sizeGroupDetail,
    [sizeGroups, selectedId, sizeGroupDetail]
  )

  const sizes = useMemo(() => {
    const list = Array.isArray(sizeGroupDetail?.sizes) ? sizeGroupDetail.sizes : []
    const q = sizeSearch.trim().toLowerCase()
    const withIndex = list.map((size, index) => ({ ...size, index }))
    if (!q) return withIndex
    return withIndex.filter((size) =>
      Object.entries(size).some(([key, value]) => {
        if (key.startsWith('_')) return false
        return formatCell(value).toLowerCase().includes(q)
      })
    )
  }, [sizeGroupDetail, sizeSearch])

  const selectGroup = (id) => {
    if (!id) setSearchParams({})
    else setSearchParams({ id })
  }

  const openCreateGroup = () => {
    setEditingGroup(null)
    groupForm.resetFields()
    groupForm.setFieldsValue({
      variant: 'default',
      isbaby: false
    })
    setGroupModalOpen(true)
  }

  const openEditGroup = () => {
    const record = sizeGroupDetail || selectedSummary
    if (!record) return
    setEditingGroup(record)
    groupForm.setFieldsValue({
      name: record.name,
      departmentId: record.departmentId || undefined,
      variant: record.variant || 'default',
      isbaby: Boolean(record.isbaby)
    })
    setGroupModalOpen(true)
  }

  const handleGroupSubmit = async (values) => {
    try {
      const body = {
        name: values.name?.trim(),
        departmentId: values.departmentId || null,
        brand: brandForApi
      }
      if (home) body.variant = values.variant || 'default'
      else body.isbaby = Boolean(values.isbaby)

      if (editingGroup?.id) {
        await updateMutation.mutateAsync({ id: editingGroup.id, body })
      } else {
        const response = await createMutation.mutateAsync(body)
        const createdId = response?.data?.data?.id
        if (createdId) selectGroup(createdId)
      }
      setGroupModalOpen(false)
      setEditingGroup(null)
      groupForm.resetFields()
    } catch (error) {
      // toast mutation içinde
    }
  }

  const handleDeleteGroup = async () => {
    if (!selectedId) return
    try {
      await deleteMutation.mutateAsync({ id: selectedId, brand: brandForApi })
      selectGroup('')
    } catch (error) {
      // toast mutation içinde
    }
  }

  const openAddSize = () => {
    setEditingSize(null)
    sizeForm.resetFields()
    sizeForm.setFieldsValue({ __extras: [] })
    setSizeModalOpen(true)
  }

  const openEditSize = (record) => {
    setEditingSize(record)
    sizeForm.resetFields()
    sizeForm.setFieldsValue(sizeToFormValues(record, fields))
    setSizeModalOpen(true)
  }

  const handleSizeSubmit = async (values) => {
    if (!selectedId) return
    try {
      const size = formValuesToSizePayload(values, brandForApi)
      const body = { size, brand: brandForApi }

      if (editingSize?._id) {
        await updateSizeRowMutation.mutateAsync({
          id: selectedId,
          sizeId: editingSize._id,
          body
        })
      } else if (editingSize && editingSize.index != null) {
        await updateSizeMutation.mutateAsync({
          id: selectedId,
          sizeIndex: editingSize.index,
          body
        })
      } else {
        await addSizeMutation.mutateAsync({ id: selectedId, body })
      }

      setSizeModalOpen(false)
      setEditingSize(null)
      sizeForm.resetFields()
    } catch (error) {
      // toast mutation içinde
    }
  }

  const handleDeleteSize = async (record) => {
    if (!selectedId) return
    try {
      if (record?._id) {
        await deleteSizeRowMutation.mutateAsync({
          id: selectedId,
          sizeId: record._id,
          brand: brandForApi
        })
        return
      }
      await deleteSizeMutation.mutateAsync({
        id: selectedId,
        sizeIndex: record.index,
        brand: brandForApi
      })
    } catch (error) {
      // toast mutation içinde
    }
  }

  const sizeColumns = [
    {
      title: '#',
      dataIndex: 'index',
      width: 56,
      render: (index) => index + 1
    },
    ...tableCols.map((col) => ({
      title: col.label,
      dataIndex: col.key,
      ellipsis: true,
      render: (value, record) => formatCell(value ?? record[col.key])
    })),
    {
      title: '',
      key: 'actions',
      width: 168,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button size="small" icon={<EditOutlined />} onClick={() => openEditSize(record)}>
            Düzenle
          </Button>
          <Popconfirm
            title="Bu bedeni silmek istiyor musunuz?"
            okText="Sil"
            cancelText="Vazgeç"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleDeleteSize(record)}
          >
            <Button size="small" danger icon={<DeleteOutlined />}>
              Sil
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ]

  const savingGroup = createMutation.isPending || updateMutation.isPending
  const savingSize =
    addSizeMutation.isPending ||
    updateSizeMutation.isPending ||
    updateSizeRowMutation.isPending

  if (isLoading) return <Spinner className="mt-20" />

  return (
    <div className="-mx-4 xl:-mx-[80px]">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/güncellemeler')}>
            Güncellemeler
          </Button>
          <MainTitle>{brandLabel(brandForApi)} beden grupları</MainTitle>
        </div>
        <div className="flex flex-wrap gap-2">
          {brandTabs.map((item) => {
            const slug = slugFromBrandName(item.name)
            const active = apiBrandFromParam(slug) === brandForApi
            return (
              <Button
                key={item.id}
                type={active ? 'primary' : 'default'}
                onClick={() => navigate(`/güncellemeler/${slug}/size-groups`)}
              >
                {item.name}
              </Button>
            )
          })}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[320px_minmax(0,1fr)]">
        <aside className="rounded-xl border border-slate-200 bg-white/90 shadow-sm">
          <div className="border-b border-slate-100 p-4">
            <div className="mb-3 flex items-center justify-between gap-2">
              <div>
                <div className="text-sm font-semibold text-slate-800">Gruplar</div>
                <div className="text-xs text-slate-500">{sizeGroups.length} kayıt</div>
              </div>
              <Button type="primary" size="small" icon={<PlusOutlined />} onClick={openCreateGroup}>
                Yeni
              </Button>
            </div>
            <Input
              allowClear
              placeholder="Ad, departman veya id ara"
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
            />
          </div>
          <div className="max-h-[70vh] overflow-y-auto p-2">
            {filteredGroups.length === 0 ? (
              <div className="px-3 py-10 text-center text-sm text-slate-500">
                Kayıt bulunamadı
              </div>
            ) : (
              filteredGroups.map((item) => {
                const active = item.id === selectedId
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => selectGroup(item.id)}
                    className={`mb-1 w-full rounded-lg border px-3 py-2.5 text-left transition ${
                      active
                        ? 'border-blue-400 bg-blue-50 shadow-sm'
                        : 'border-transparent hover:border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="truncate font-medium text-slate-800">{item.name}</div>
                        <div className="mt-0.5 truncate text-xs text-slate-500">
                          {item.Department?.name || 'Departman yok'}
                          {home && item.variant ? ` · ${item.variant}` : ''}
                        </div>
                      </div>
                      <Tag color={active ? 'blue' : 'default'}>{groupSizeCount(item)}</Tag>
                    </div>
                  </button>
                )
              })
            )}
          </div>
        </aside>

        <section className="rounded-xl border border-slate-200 bg-white/90 p-5 shadow-sm">
          {!selectedId ? (
            <div className="flex min-h-[420px] flex-col items-center justify-center text-center">
              <div className="mb-2 text-lg font-semibold text-slate-700">Bir beden grubu seçin</div>
              <p className="mb-4 max-w-md text-sm text-slate-500">
                Soldan grubu tıklayın. Bedenler child tablodan gelir; JSON veya Excel gerekmez.
              </p>
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreateGroup}>
                Yeni grup ekle
              </Button>
            </div>
          ) : detailLoading && !sizeGroupDetail ? (
            <Spinner className="mt-24" />
          ) : (
            <>
              <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="text-xl font-semibold text-slate-800">
                    {sizeGroupDetail?.name || selectedSummary?.name}
                  </h2>
                  <div className="mt-2 flex flex-wrap gap-2 text-sm">
                    <Tag>{sizeGroupDetail?.Department?.name || 'Departman yok'}</Tag>
                    {home ? (
                      <Tag color="purple">{sizeGroupDetail?.variant || 'default'}</Tag>
                    ) : (
                      <Tag color={sizeGroupDetail?.isbaby ? 'gold' : 'default'}>
                        {sizeGroupDetail?.isbaby ? 'Bebek' : 'Bebek değil'}
                      </Tag>
                    )}
                    <Tag color="blue">
                      {Array.isArray(sizeGroupDetail?.sizes) ? sizeGroupDetail.sizes.length : 0} beden
                    </Tag>
                  </div>
                  <div className="mt-2 font-mono text-xs text-slate-400">{selectedId}</div>
                </div>
                <Space wrap>
                  <Button icon={<EditOutlined />} onClick={openEditGroup}>
                    Grubu düzenle
                  </Button>
                  <Popconfirm
                    title="Grubu silmek istiyor musunuz?"
                    description="Siparişte kullanılan grup silinemez. Beden satırları da silinir."
                    okText="Sil"
                    cancelText="Vazgeç"
                    okButtonProps={{ danger: true }}
                    onConfirm={handleDeleteGroup}
                  >
                    <Button danger icon={<DeleteOutlined />} loading={deleteMutation.isPending}>
                      Grubu sil
                    </Button>
                  </Popconfirm>
                </Space>
              </div>

              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <Input
                  allowClear
                  placeholder="Bedenlerde ara"
                  value={sizeSearch}
                  onChange={(event) => setSizeSearch(event.target.value)}
                  style={{ maxWidth: 280 }}
                />
                <Button type="primary" icon={<PlusOutlined />} onClick={openAddSize}>
                  Beden ekle
                </Button>
              </div>

              <Table
                rowKey={(row) => row._id || `${row.uid || row.scode || 'size'}-${row.index}`}
                size="small"
                columns={sizeColumns}
                dataSource={sizes}
                pagination={{ pageSize: 15, showSizeChanger: true, showTotal: (total) => `${total} beden` }}
                scroll={{ x: 980 }}
                locale={{ emptyText: 'Bu grupta henüz beden yok. Beden ekle ile satır oluşturun.' }}
                onRow={(record) => ({
                  onDoubleClick: () => openEditSize(record)
                })}
              />
            </>
          )}
        </section>
      </div>

      <Modal
        title={editingGroup ? 'Beden grubunu düzenle' : 'Yeni beden grubu'}
        open={groupModalOpen}
        onCancel={() => {
          setGroupModalOpen(false)
          setEditingGroup(null)
          groupForm.resetFields()
        }}
        onOk={() => groupForm.submit()}
        okText={editingGroup ? 'Kaydet' : 'Oluştur'}
        cancelText="Vazgeç"
        confirmLoading={savingGroup}
        destroyOnClose
      >
        <Form form={groupForm} layout="vertical" onFinish={handleGroupSubmit} className="pt-2">
          <Form.Item name="name" label="Grup adı" rules={[{ required: true, message: 'Ad zorunlu' }]}>
            <Input placeholder="Örn. WOMAN TRF" />
          </Form.Item>
          <Form.Item name="departmentId" label="Departman">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Departman seçin"
              options={(departments || []).map((item) => ({
                value: item.id,
                label: item.name
              }))}
            />
          </Form.Item>
          {home ? (
            <Form.Item name="variant" label="Variant">
              <Input placeholder="default" />
            </Form.Item>
          ) : (
            <Form.Item name="isbaby" label="Bebek grubu" valuePropName="checked">
              <Switch />
            </Form.Item>
          )}
        </Form>
      </Modal>

      <Modal
        title={editingSize ? 'Bedeni düzenle' : 'Yeni beden'}
        open={sizeModalOpen}
        onCancel={() => {
          setSizeModalOpen(false)
          setEditingSize(null)
          sizeForm.resetFields()
        }}
        onOk={() => sizeForm.submit()}
        okText={editingSize ? 'Kaydet' : 'Ekle'}
        cancelText="Vazgeç"
        confirmLoading={savingSize}
        width={920}
        destroyOnClose
        style={{ top: 24 }}
      >
        <Form form={sizeForm} layout="vertical" onFinish={handleSizeSubmit} className="pt-2">
          <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2 lg:grid-cols-3">
            {fields.map((field) => (
              <Form.Item
                key={field.name}
                name={field.name}
                label={field.label}
                tooltip={field.hint}
              >
                <Input
                  placeholder={field.label}
                  disabled={Boolean(editingSize && field.name === 'uid' && !home)}
                />
              </Form.Item>
            ))}
          </div>

          <div className="mb-2 text-sm font-medium text-slate-700">Ek alanlar</div>
          <Form.List name="__extras">
            {(rows, { add, remove }) => (
              <div className="space-y-2">
                {rows.map((row) => (
                  <div key={row.key} className="grid grid-cols-[1fr_1fr_auto] gap-2">
                    <Form.Item name={[row.name, 'key']} className="mb-0">
                      <Input placeholder="alan adı" />
                    </Form.Item>
                    <Form.Item name={[row.name, 'value']} className="mb-0">
                      <Input placeholder="değer" />
                    </Form.Item>
                    <Button danger onClick={() => remove(row.name)}>
                      Sil
                    </Button>
                  </div>
                ))}
                <Button type="dashed" onClick={() => add({ key: '', value: '' })}>
                  Alan ekle
                </Button>
              </div>
            )}
          </Form.List>
        </Form>
      </Modal>
    </div>
  )
}
















